import React from "react";
import {
  Box,
  Container,
  Divider,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useChangePassword } from "../../hooks/api/useProfile";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { PasswordFormData } from "../Profile/types/profileTypes";
import AppButton from "../common/AppButton";
import {
  authFormCardSx,
  authFormInputSx,
  authFormStagger,
  authFormItemMotion
} from "../common/constants/authFormStyles";

interface LoginChangePasswordProps {
  onChangeSuccess: () => void;
}

const LoginChangePassword: React.FC<LoginChangePasswordProps> = ({
  onChangeSuccess
}) => {
  const { t } = useKivunovaTranslation();
  const changePassword = useChangePassword();

  const schema = Yup.object({
    currentPassword: Yup.string().required(
      t("current-password-required", "Current password is required")
    ),
    newPassword: Yup.string()
      .required(t("new-password-required", "New password is required"))
      .min(8, t("new-password-min", "Password must be at least 8 characters")),
    confirmPassword: Yup.string()
      .required(t("confirm-password-required", "Please confirm your password"))
      .oneOf(
        [Yup.ref("newPassword")],
        t("passwords-must-match", "Passwords must match")
      )
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PasswordFormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });

  const onSubmit = async (data: PasswordFormData) => {
    try {
      await changePassword.mutateAsync(
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword
        },
        {
          onSuccess: () => {
            onChangeSuccess();
          }
        }
      );
      reset();
    } catch (err) {
      console.error("Password change failed:", err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <Paper elevation={0} sx={authFormCardSx} data-testid="login-right-panel">
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <motion.div
            variants={authFormStagger}
            initial="initial"
            animate="animate"
            style={{ width: "100%" }}
          >
            <motion.div variants={authFormItemMotion}>
              <Typography
                variant="h6"
                fontWeight={600}
                color="text.primary"
                textAlign="center"
                sx={{ mb: 1 }}
              >
                {t("change-password-title", "Change Password")}
              </Typography>
              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 450 }}
              >
                {t(
                  "change-password-description",
                  "Update your account password below. Make sure your new password is strong and secure."
                )}
              </Typography>
              <Divider sx={{ mb: 3, borderColor: "divider" }} />
            </motion.div>

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 2,
                gap: 2.5,
                width: "100%",
                margin: "auto"
              }}
            >
              <motion.div variants={authFormItemMotion}>
                <Controller
                  name="currentPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label={t("current-password", "Current Password")}
                      error={!!errors.currentPassword}
                      helperText={errors.currentPassword?.message}
                      sx={authFormInputSx}
                    />
                  )}
                />
              </motion.div>
              <motion.div variants={authFormItemMotion}>
                <Controller
                  name="newPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label={t("new-password", "New Password")}
                      error={!!errors.newPassword}
                      helperText={errors.newPassword?.message}
                      sx={authFormInputSx}
                    />
                  )}
                />
              </motion.div>
              <motion.div variants={authFormItemMotion}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label={t("confirm-password", "Confirm New Password")}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      sx={authFormInputSx}
                    />
                  )}
                />
              </motion.div>
              <motion.div variants={authFormItemMotion}>
                <AppButton
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={changePassword.isPending}
                  sx={{ py: 1.25, borderRadius: 2, mt: 1 }}
                >
                  {changePassword.isPending
                    ? t("processing", "Processing...")
                    : t("change-password", "Change Password")}
                </AppButton>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Paper>
    </motion.div>
  );
};

export default LoginChangePassword;
